import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { Observable, BehaviorSubject, NEVER, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { switchMap, map, filter, take, tap } from 'rxjs/operators';

import { ButtonTheme, ButtonMode } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { CmsTeaser } from '@fcom/core-api';
import { AppState } from '@fcom/core/interfaces';
import { pseudoLocal } from '@fcom/core/selectors';
import { CmsDataService } from '@fcom/core/services';
import { finShare } from '@fcom/rx';
import { ConfigService } from '@fcom/core';

import { Consent, ConsentData, ConsentGroup, ConsentStatus, ConsentTextId } from '../../interfaces';
import { GtmService } from '../../gtm/';
import { ConsentService } from '../../login';

@Component({
  selector: 'fin-cookie-policy',
  styleUrls: ['./cookie-policy.component.scss'],
  templateUrl: './cookie-policy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CookiePolicyComponent implements OnInit {
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly ConsentStatus = ConsentStatus;
  cookiePolicy$: Observable<CmsTeaser>;
  consents$: Observable<Consent[]>;
  continueShows$: Observable<boolean>;
  modalOpen = false;
  isShowCookieSettings = false;

  private hasCookieConsentSubmitted$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private consentGroup$: Observable<ConsentGroup>;

  constructor(
    private consentService: ConsentService,
    private languageService: LanguageService,
    private cmsDataService: CmsDataService,
    private gtmService: GtmService,
    private store$: Store<AppState>,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.continueShows$ = this.store$.pipe(pseudoLocal('continueShows'));
    this.consentService.injectCookieSnippet();
    // @TODO: Pull new article once available
    this.consentGroup$ = this.consentService.cookieConsents$;

    this.consents$ = this.consentGroup$.pipe(
      map((cg) => {
        return [
          {
            consentTextId: ConsentTextId.NOT_REAL_CONSENT_VALUE,
            effectiveFrom: -1,
            text: cg.introductionText,
            textHash: '',
            consentStatus: ConsentStatus.WITHDRAWN,
          },
        ].concat(cg.consents);
      }),
      finShare()
    );

    this.cookiePolicy$ = this.consentGroup$.pipe(
      filter((cg) => this.configService.cfg.enableCookiePolicy && !this.allConsentsAreSet(cg)),
      tap(() => {
        this.modalOpen = true;
      }),
      switchMap(() => this.languageService.translate('fragments.cookiePolicy.url')),
      switchMap((url) => (!url ? NEVER : this.cmsDataService.getFragmentJson(url))),
      take(1),
      finShare()
    ) as Observable<CmsTeaser>;

    this.consentGroup$
      .pipe(
        take(1),
        filter((cg) => this.allConsentsAreSet(cg)),
        map((cg) => this.createConsentData(cg))
      )
      .subscribe((consentData: ConsentData) => {
        this.gtmService.setConsents(consentData);
      });
  }

  allConsentsAreSet(consentGroup: ConsentGroup): boolean {
    return consentGroup?.consents?.every((c) => c.consentStatus !== ConsentStatus.WITHDRAWN);
  }

  confirmAndClose(consentData?: ConsentData): void {
    this.consentService.setCookieConsents(consentData);
    this.hasCookieConsentSubmitted$.next(true);
    this.closeModal();
  }

  quickSetCookies(type: ConsentStatus): void {
    combineLatest([this.consentGroup$, this.hasCookieConsentSubmitted$])
      .pipe(
        map(([cg, hasSubmitted]) => (!hasSubmitted ? cg : null)),
        filter(Boolean),
        take(1),
        map((cg) => ({
          ...cg,
          consents: cg.consents?.map((c) => ({
            ...c,
            consentStatus: type,
          })),
        })),
        map((cg: ConsentGroup) => this.createConsentData(cg))
      )
      .subscribe((consentData: ConsentData) => {
        this.consentService.setCookieConsents(consentData);
        this.hasCookieConsentSubmitted$.next(true);
      });
    this.closeModal();
  }

  showCookieSettings(): void {
    this.isShowCookieSettings = true;
  }

  openModal(event: MouseEvent): void {
    event.stopPropagation();
    this.modalOpen = true;
  }

  closeModal(): void {
    this.modalOpen = false;
  }

  private createConsentData(consentGroup: ConsentGroup): ConsentData {
    return consentGroup?.consents.reduce((all, consent) => {
      all[consent.consentTextId] = consent.consentStatus !== ConsentStatus.DECLINED;
      return all;
    }, {});
  }
}
